import { useEffect, useState } from "react";
import { HelpCircle, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { refreshSiteContent } from "../../lib/site";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import ConfirmDialog from "../ConfirmDialog";

const FAQ_CATS_TR = [
    ["platform", "Platform"],
    ["supplier", "Tedarikçiler"],
    ["store", "Mağaza"],
];

export default function FaqTab() {
    const [items, setItems] = useState(null);
    const [edits, setEdits] = useState({});
    const [newQ, setNewQ] = useState("");
    const [newA, setNewA] = useState("");
    const [newCat, setNewCat] = useState("platform");
    const [busy, setBusy] = useState(false);
    const [confirmDel, setConfirmDel] = useState(null);

    const load = () =>
        api.get("/admin/faq").then((r) => {
            setItems(r.data);
            setEdits({});
        }).catch(() => {});
    useEffect(() => { load(); }, []);

    const saveItem = async (n) => {
        const e = edits[n];
        if (!e) return;
        try {
            await api.put(`/admin/faq/${n}`, e);
            toast.success("SSS güncellendi — ana sayfada anında yayında");
            refreshSiteContent();
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Kaydedilemedi");
        }
    };

    const add = async () => {
        if (!newQ.trim() || !newA.trim()) return toast.error("Soru ve cevap zorunludur");
        setBusy(true);
        try {
            await api.post("/admin/faq", { question: newQ.trim(), answer: newA.trim(), category: newCat });
            toast.success("Yeni SSS eklendi — ana sayfada yayında");
            setNewQ("");
            setNewA("");
            setNewCat("platform");
            refreshSiteContent();
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Eklenemedi");
        } finally {
            setBusy(false);
        }
    };

    const del = async () => {
        const n = confirmDel;
        setConfirmDel(null);
        try {
            await api.delete(`/admin/faq/${n}`);
            toast.success("SSS silindi");
            refreshSiteContent();
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Silinemedi");
        }
    };

    if (!items) return <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-24 animate-pulse rounded-2xl bg-muted" />)}</div>;

    return (
        <div className="space-y-6" data-testid="faq-tab">
            <p className="text-sm text-muted-foreground">
                Ana sayfadaki "Sık Sorulan Sorular" bölümünü buradan yönetin. Değişiklikler anında yayına alınır.
            </p>

            <div className="space-y-4">
                {items.map((it) => {
                    const e = edits[it.n] || { question: it.question, answer: it.answer, category: it.category };
                    const dirty = e.question !== it.question || e.answer !== it.answer || e.category !== it.category;
                    return (
                        <div key={it.n} className="rounded-2xl border bg-card p-5 shadow-sm" data-testid={`faq-row-${it.n}`}>
                            <div className="mb-2 flex items-center justify-between">
                                <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                    <HelpCircle className="h-3.5 w-3.5 text-terra-600" /> Soru {it.n}
                                </p>
                                <select
                                    value={e.category}
                                    onChange={(ev) => setEdits({ ...edits, [it.n]: { ...e, category: ev.target.value } })}
                                    className="h-7 rounded-md border bg-background px-2 text-xs"
                                    data-testid={`faq-category-${it.n}`}
                                >
                                    {FAQ_CATS_TR.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                </select>
                                <div className="flex gap-2">
                                    <Button size="sm" onClick={() => saveItem(it.n)} disabled={!dirty} className="bg-terra-600 text-white hover:bg-terra-700" data-testid={`faq-save-${it.n}`}>
                                        <Save className="mr-1 h-3.5 w-3.5" /> Kaydet
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => setConfirmDel(it.n)} className="text-destructive hover:text-destructive" data-testid={`faq-delete-${it.n}`}>
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            </div>
                            <Input
                                value={e.question}
                                onChange={(ev) => setEdits({ ...edits, [it.n]: { ...e, question: ev.target.value } })}
                                placeholder="Soru"
                                className="mb-2 font-medium"
                                data-testid={`faq-question-input-${it.n}`}
                            />
                            <Textarea
                                rows={2}
                                value={e.answer}
                                onChange={(ev) => setEdits({ ...edits, [it.n]: { ...e, answer: ev.target.value } })}
                                placeholder="Cevap"
                                data-testid={`faq-answer-input-${it.n}`}
                            />
                        </div>
                    );
                })}
                {items.length === 0 && <p className="text-sm text-muted-foreground">Henüz SSS kaydı yok. Aşağıdan ilk soruyu ekleyin.</p>}
            </div>

            <div className="rounded-2xl border border-dashed bg-card p-5" data-testid="faq-add-form">
                <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Yeni Soru Ekle</p>
                <select value={newCat} onChange={(e) => setNewCat(e.target.value)} className="mb-2 h-10 w-full rounded-md border bg-background px-3 text-sm sm:w-56" data-testid="faq-new-category">
                    {FAQ_CATS_TR.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <Input value={newQ} onChange={(e) => setNewQ(e.target.value)} placeholder="Soru (örn: Üyelik ücretli mi?)" className="mb-2" data-testid="faq-new-question" />
                <Textarea rows={2} value={newA} onChange={(e) => setNewA(e.target.value)} placeholder="Cevap" data-testid="faq-new-answer" />
                <Button onClick={add} disabled={busy} className="mt-3 bg-terra-600 text-white hover:bg-terra-700" data-testid="faq-add-button">
                    {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />} Soruyu Ekle
                </Button>
            </div>

            <ConfirmDialog
                open={confirmDel !== null}
                onClose={() => setConfirmDel(null)}
                onConfirm={del}
                title="SSS kaydı silinsin mi?"
                description="Bu soru-cevap ana sayfadan kaldırılır. Bu işlem geri alınamaz."
                confirmText="Evet, Sil"
                destructive
                testId="faq-delete-dialog"
            />
        </div>
    );
}
